import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    Input,
    OnInit,
    signal,
    ViewChild,
} from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { OptBean } from "../s25-opt/s25.opt.component";
import { S25ObjectDetailsService } from "./s25.object.details.service";
import { Bind } from "../../decorators/bind.decorator";
import { Item } from "../../pojo/Item";
import { Contact } from "../../pojo/Contact";
import { S25LoadingApi } from "../s25-loading/loading.api";
import { Panel } from "../s25-panel/s25.panels.component";
import { OrgDetailData } from "../../pojo/OrganizationI";
import { EventDetailData } from "../s25-event/EventMicroI";
import { S25EventDetailsInlineEditService } from "./event/s25.event.details.inline.edit.service";
import { NotificationService } from "../../services/notification.service";
import { S25Util } from "../../util/s25-util";
import { BpeService } from "../bpe/bpe.service";

@TypeManagerDecorator("s25-ng-object-details")
@Component({
    selector: "s25-ng-object-details",
    template: `
        <h2 class="visually-hidden">Details</h2>
        @if (optBean()) {
            <s25-ng-opt [modelBean]="optBean()!"></s25-ng-opt>
        }
        <div #loadingEl>
            <s25-loading-inline [model]="{}"></s25-loading-inline>
        </div>
        @if (init()) {
            @if (showPostSaveActions()) {
                <div class="silent-save-controls">
                    <s25-ng-checkbox
                        [value]="inlineEditService.silentSave()"
                        (valueChange)="inlineEditService.setSilentSave(itemId, $event)"
                    >
                        Silent Save
                    </s25-ng-checkbox>
                    <s25-ng-info-message [isPopover]="true" [customLabel]="'Silent Save'">
                        Inline Edits are saved without triggering email notifications. Use
                        <strong>Post-Save Actions</strong> when you're finished to process all changes and send related
                        emails at once.
                    </s25-ng-info-message>
                    @if (inlineEditService.silentSave()) {
                        <button
                            type="button"
                            class="aw-button aw-button--outline"
                            [disabled]="
                                !inlineEditService.hasPendingScenarioChanges() ||
                                inlineEditService.runningDeferredScenarios()
                            "
                            (click)="runDeferredScenarios()"
                        >
                            Post-Save Actions
                        </button>
                        <s25-ng-info-message [isPopover]="true" [customLabel]="'Post-Save Actions'">
                            Processes notifications for all changes made on this page, based on the event state before
                            those edits.
                        </s25-ng-info-message>
                    }
                </div>
            }
            @let warningMessage = data?.warningMessage;
            @if (warningMessage) {
                <div #warningMsgEl class="cn-alert cn-alert--warning">
                    {{ warningMessage.message }}
                    @if (warningMessage.link) {
                        View the&nbsp;<a [href]="warningMessage.link">help page</a>&nbsp;for more information.
                    }
                    <button
                        class="c-textButton"
                        (click)="warningMsgEl.classList.toggle('ngHidden')"
                        aria-label="Dismiss"
                    >
                        <s25-ng-icon [type]="'close'" [ariaLabel]="false" />
                    </button>
                </div>
            }
            <div class="details-wrapper">
                <s25-ng-panels [panels]="panels"> </s25-ng-panels>
            </div>
            @if (data?.dateStamp) {
                <p class="s25-pubdate">Information is current as of {{ data.dateStamp }}</p>
            }
        }
    `,
    styles: `
        .silent-save-controls {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: 0.25rem;
            flex-wrap: wrap;
            margin: 0 0 1rem;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
    providers: [S25EventDetailsInlineEditService],
})
export class S25ObjectDetailsComponent implements OnInit {
    @Input({ required: true }) itemId: number;
    @Input({ required: true }) itemTypeId: Item.Id;

    @ViewChild("loadingEl") loadingEl: ElementRef;

    init = signal(false);
    optBean = signal<OptBean | null>(null);
    data: ObjDetailsData;
    panels: Panel[] = [];
    readonly canEdit = signal(false);
    readonly hasInlineEditScenarios = signal(false);
    readonly showPostSaveActions = computed(
        () => this.init() && this.itemTypeId === Item.Ids.Event && this.canEdit() && this.hasInlineEditScenarios(),
    );

    readonly objectDetailsService = inject(S25ObjectDetailsService);
    readonly inlineEditService = inject(S25EventDetailsInlineEditService);

    async ngOnInit() {
        const optBean = await S25ObjectDetailsService.getOptBean(this.itemId, this.itemTypeId);
        this.optBean.set({
            ...optBean,
            autoInit: false,
            refreshF: this.refresh,
        });
        await this.initObjectDetails();
    }

    @Bind
    refresh() {
        this.init.set(false);
        S25ObjectDetailsService.invalidateCache(this.itemTypeId);
        return this.initObjectDetails();
    }

    async initObjectDetails() {
        S25LoadingApi.init(this.loadingEl.nativeElement);
        this.data = (await this.objectDetailsService.getObjDetailsData(this.itemId, this.itemTypeId)) ?? { panels: [] };
        this.panels = this.data.panels;
        const isEditableEvent = this.itemTypeId === Item.Ids.Event && !!this.data.canEdit;
        this.canEdit.set(!!this.data.canEdit);
        if (isEditableEvent) {
            const inlineEditSources = await BpeService.getInlineEditScenarioSources();
            const hasInlineEditScenarios = inlineEditSources["event-details-inline-edit"];
            this.hasInlineEditScenarios.set(hasInlineEditScenarios);
            if (hasInlineEditScenarios) {
                this.inlineEditService.initForEventDetailsInlineEdit(this.itemId);
            } else {
                this.inlineEditService.silentSave.set(false);
                this.inlineEditService.clearDeferredScenarioState();
            }
        }
        S25LoadingApi.destroy(this.loadingEl.nativeElement);
        this.init.set(true);
    }

    async runDeferredScenarios() {
        if (!this.inlineEditService.hasPendingScenarioChanges() || this.inlineEditService.runningDeferredScenarios()) {
            return;
        }
        try {
            await this.inlineEditService.runDeferredInlineEditScenarios(this.itemId);
            NotificationService.post("Scenarios have been run for your changes.");
        } catch (caughtError) {
            S25Util.showError(caughtError, "There was an error while running scenarios.");
        }
    }
}

export type ObjDetailsData = {
    panels: Panel[];
    dateStamp?: string;
    canEdit?: boolean;
    warningMessage?: { message: string; link?: string };
    general?: Contact.DetailData["general"] | OrgDetailData["general"] | EventDetailData["general"];
    userAccount?: Contact.DetailData["userAccount"];
};
